import React from 'react';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, KPICard, Card, Badge, Table, FeaturePlaceholder } from '@/components/ui';
import { usePerformance } from './hooks/usePerformance';
import { useAuth } from '@/core/auth/AuthContext';

export default function MaintenancePerformancePage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const {
    stats,
    teamRankings,
    zonePerformance,
    trend,
    isLoading,
    filters,
    setFilters,
  } = usePerformance(companyId);

  const riskColors: Record<string, string> = {
    low: 'bg-emerald-100 text-emerald-600',
    medium: 'bg-amber-100 text-amber-600',
    high: 'bg-orange-100 text-orange-600',
    critical: 'bg-red-100 text-red-600',
  };

  return (
    <PageLayout
      title="Hiệu suất Vận hành"
      description="Phân tích chất lượng, tiến độ và hiệu quả chi phí của toàn bộ hoạt động bảo dưỡng cảnh quan."
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Hiệu suất' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Xuất báo cáo (PDF)</Button>
          <Button variant="primary" icon="ri-download-line">Dữ liệu Excel</Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Top KPI Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Điểm hiệu suất"
            value={stats?.performanceScore || 0}
            icon="ri-dashboard-3-line"
            variant="primary"
            suffix="/10"
          />
          <KPICard
            title="Tỉ lệ hoàn thành"
            value={stats?.completionRate || 0}
            icon="ri-checkbox-circle-line"
            variant="success"
            suffix="%"
          />
          <KPICard
            title="Đúng hạn (On-time)"
            value={stats?.onTimeRate || 0}
            icon="ri-time-line"
            variant="warning"
            suffix="%"
          />
          <KPICard
            title="Tuân thủ SLA"
            value={stats?.slaComplianceRate || 0}
            icon="ri-shield-check-line"
            variant="info"
            suffix="%"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-wrap gap-3">
            <Filter
              options={[
                {
                  id: 'period', label: 'Thời gian', type: 'select', choices: [
                    { value: 'week', label: 'Tuần này' },
                    { value: 'month', label: 'Tháng này' },
                    { value: 'quarter', label: 'Quý này' },
                    { value: 'year', label: 'Năm nay' },
                  ]
                },
                { id: 'project', label: 'Dự án', type: 'select', choices: [{ value: 'all', label: 'Tất cả dự án' }] }
              ]}
              onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
            />
          </div>
        </PageLayout.Toolbar>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trend Chart Placeholder */}
          <Card className="lg:col-span-2 p-6 flex flex-col min-h-[400px]">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
              <i className="ri-line-chart-line text-os-primary" />
              Xu hướng Hiệu suất & Chất lượng
            </h3>
            <div className="flex-1 flex items-center justify-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <div className="text-center">
                <i className="ri-bar-chart-2-line text-4xl text-slate-300 mb-2 block" />
                <p className="text-sm text-slate-400 font-medium">Biểu đồ xu hướng (Planned vs Actual)</p>
                <p className="text-[10px] text-slate-400">Đang đồng bộ hóa dữ liệu từ Work Orders...</p>
              </div>
            </div>
          </Card>

          {/* Team Ranking */}
          <Card className="p-6">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
              <i className="ri-medal-line text-os-primary" />
              Xếp hạng Tổ đội
            </h3>
            <div className="space-y-4">
              {teamRankings.slice(0, 5).map((team) => (
                <div key={team.teamId} className="flex items-center gap-4 p-3 bg-slate-50 rounded-2xl border border-transparent hover:border-os-primary/30 transition-all cursor-pointer">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${team.rank === 1 ? 'bg-warning-100 text-warning-700' : 'bg-slate-200 text-slate-600'}`}>
                    {team.rank}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">{team.teamName}</p>
                    <div className="flex gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-500 font-medium">Hoàn thành: {team.completionRate}%</span>
                      <span className="text-slate-300 text-[10px]">•</span>
                      <span className="text-[10px] text-os-primary font-bold">QA: {team.qaScore.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-slate-700">{team.totalScore}</p>
                    <p className="text-[9px] text-slate-400  font-bold tracking-tighter">Điểm tổng</p>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" className="w-full mt-4 text-os-primary text-xs font-bold">Xem toàn bộ bảng xếp hạng</Button>
          </Card>
        </div>

        {/* Zone Health / Performance Table */}
        <Card className="overflow-hidden border-none shadow-sm">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <i className="ri-heart-pulse-line text-os-primary" />
              Sức khỏe vận hành theo Khu vực (Zone Health)
            </h3>
            <Badge variant="neutral" className="bg-slate-100 text-slate-600">{zonePerformance.length} Khu vực</Badge>
          </div>
          <Table
            columns={[
              { key: 'zone', title: 'Khu vực (Zone)' },
              { key: 'completion', title: 'Hoàn thành (%)', align: 'center' },
              { key: 'qa', title: 'Điểm QA', align: 'center' },
              { key: 'issues', title: 'Sự cố', align: 'center' },
              { key: 'cost', title: 'Chi phí/m2', align: 'right' },
              { key: 'risk', title: 'Rủi rỏ', align: 'center' },
            ]}
            data={zonePerformance}
            isLoading={isLoading}
            renderCell={(item, key) => {
              switch (key) {
                case 'zone':
                  return <span className="font-bold text-slate-900">{item.zoneName}</span>;
                case 'completion':
                  return <span className="font-bold text-emerald-600">{item.completionRate}%</span>;
                case 'qa':
                  return <span className="font-bold text-warning-600">{item.qaScore.toFixed(1)}</span>;
                case 'issues':
                  return <span className={`font-bold ${item.issuesCount > 0 ? 'text-red-500' : 'text-slate-400'}`}>{item.issuesCount}</span>;
                case 'cost':
                  return <span className="text-xs font-bold text-slate-700">{item.costPerSqm.toLocaleString()} đ</span>;
                case 'risk':
                  return (
                    <Badge className={`${riskColors[item.riskLevel]} border-transparent text-[10px] font-bold `}>
                      {item.riskLevel}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
